<script setup lang="ts">
import type { Category } from "~/models/Category"
import { capitalizeFirstLetter } from "~/common/helpers"

/**
 *
 *  Props and emits section
 *
 *
*/

const props = defineProps<{
    category: Category;
    parentPath?: Category[];
    isExpanded: (category: Category) => boolean;
    getCategoryId: (category: Category) => string;
    isCategorySelected: (category: Category) => string;
}>()

const emit = defineEmits<{
    (e: 'selectCategory', category: Category, parentCategory?: Category): void;
    (e: 'toggleSubcategory', event: Event, category: Category): void;
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
*/

const immediateParent = computed(() => {
    if (props.parentPath && props.parentPath.length > 0) {
        return props.parentPath[props.parentPath.length - 1];
    }
    return undefined;
})

/**
 *
 *  Life cicle events
 *
 *
*/

/**
 *
 *  Functions section
 *
 *
*/

function handleSelection() {
    emit('selectCategory', props.category, immediateParent.value);
}

function handleToggle(event: Event) {
    emit('toggleSubcategory', event, props.category);
}
</script>

<template>
    <ul>
        <li 
            v-if="category.children.length === 0"
            class="custom-category-item"
            @click="handleSelection"
        >
            <span :class="`custom-item-label ${isCategorySelected(category)}`">
                {{ capitalizeFirstLetter(category.label ?? "") }}
            </span>
        </li>
        
        <li 
            v-else 
            :class="`custom-category-parent-container ${isExpanded(category) ? 'is-expanded' : ''}`" 
        >
            <div class="custom-category-item-parent" @click="handleSelection">
                <span 
                    :class="`custom-item-label ${isCategorySelected(category)}`"
                >
                    {{ capitalizeFirstLetter(category.label ?? "") }}
                </span>
                
                <div class="tw-rounded-full tw-flex tw-items-center hover:tw-bg-customGray-200 tw-p-1" @click.stop="handleToggle($event)">
                    <q-icon
                        class="tw-cursor-pointer expand-icon hover:tw-scale-125 transition-all duration-300"
                        :name="isExpanded(category) ? 'fa-solid fa-angle-down' : 'fa-solid fa-angle-right'"
                        size="16px"
                    />
                </div>
            </div>

            <div 
                :class="`custom-subcategory-container ${isExpanded(category) ? 'tw-block translateUp-dropdown' : 'tw-hidden'}`"
                :id="getCategoryId(category)"
            >
                <ul class="custom-subcategory-list">
                    <CustomDropdownItem
                        v-for="(child, index) in category.children" 
                        :key="index"
                        :category="child"
                        :parentPath="[...(parentPath || []), category]"
                        :isExpanded="isExpanded"
                        :getCategoryId="getCategoryId"
                        :isCategorySelected="isCategorySelected"
                        @selectCategory="(cat, parent) => $emit('selectCategory', cat, parent)"
                        @toggleSubcategory="(event, cat) => $emit('toggleSubcategory', event, cat)"
                    />
                </ul>
            </div>
        </li>
    </ul>
</template>

<style scoped>
.translateUp-dropdown {
    animation-name: translateUp;
    animation-duration: .5s;
    animation-timeline: view();
    animation-range: entry 10% cover 20%;
}

.custom-category-item, .custom-category-item-parent {
    padding: 8px 12px;
    transition: background-color 0.2s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.custom-category-item:hover, .custom-category-item-parent:hover {
    background-color: rgba(0,0,0,0.03);
}

.custom-item-label {
    font-size: 0.875rem;
    line-height: 1.25rem;
}

@media (min-width: 1024px) {
    .custom-item-label {
        font-size: 1rem;
        line-height: 1.5rem;
    }
}

.custom-category-parent-container {
    position: relative;
    width: 100%;
}

.custom-expand-icon {
    transition: transform 0.2s ease;
}

.custom-subcategory-container {
    position: absolute;
    left: 100%;
    top: 0;
    min-width: 200px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    z-index: 100;
    overflow: visible !important;
}

.custom-subcategory-list {
    list-style-type: none;
    padding: 4px 0;
    margin: 0;
    overflow: visible !important;
    width: 100%;
}

@media (max-width: 768px) {
    .custom-subcategory-container {
        position: static;
        left: auto;
        box-shadow: none;
        padding-left: 16px;
    }
}
</style> 